import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useWorldState } from "../components/state/WorldStateContext.jsx";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
// Icons removed - text-only UI
import ProfitChart from "../components/Profitchart";
import NewsTicker from "../components/ui/NewsTicker.jsx";

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

function formatCurrency(amount, showSign = false) {
  const absAmount = Math.abs(amount || 0);
  let formatted;
  
  if (absAmount >= 1000000) {
    formatted = `$${(absAmount / 1000000).toFixed(2)}M`;
  } else if (absAmount >= 1000) {
    formatted = `$${(absAmount / 1000).toFixed(1)}K`;
  } else {
    formatted = `$${absAmount.toFixed(0)}`;
  }
  
  if (showSign && amount !== 0) {
    return amount > 0 ? `+${formatted}` : `-${formatted.slice(1)}`;
  }
  return amount < 0 ? `-${formatted.slice(1)}` : formatted;
}

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function FinancialsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { worldState } = useWorldState();
  const [selectedTab, setSelectedTab] = useState("today");

  // Early return if no world state yet
  if (!worldState) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#0a0a0a", color: "#737373" }}>
        {t('financials.loading')}
      </div>
    );
  }

  // Get all flights with financial data
  const allFlights = worldState?.flights || [];
  
  // Completed flights (have earnings)
  const completedFlights = useMemo(() => {
    return allFlights
      .filter(f => f.phase === "completed" && (f.revenue || f.costs))
      .sort((a, b) => new Date(b.completedAt || b.scheduledDeparture) - new Date(a.completedAt || a.scheduledDeparture));
  }, [allFlights]);

  // Cancelled flights (have cancellation costs)
  const cancelledFlights = useMemo(() => {
    return allFlights
      .filter(f => f.status === "cancelled" || f.phase === "cancelled")
      .sort((a, b) => new Date(b.cancelledAt || b.scheduledDeparture) - new Date(a.cancelledAt || a.scheduledDeparture));
  }, [allFlights]);

  // Emergency landed/crashed flights
  const emergencyFlights = useMemo(() => {
    return allFlights
      .filter(f => f.status === "emergency_landed" || f.phase === "emergency_landed" ||
                   f.status === "crashed" || f.phase === "crashed")
      .sort((a, b) => new Date(b.completedAt || b.scheduledDeparture) - new Date(a.completedAt || a.scheduledDeparture));
  }, [allFlights]);

  // Calculate today's financials
  const todayStats = useMemo(() => {
    const today = new Date(worldState?.gameTime || Date.now());
    const startOfDay = new Date(today);
    startOfDay.setUTCHours(0, 0, 0, 0);
    
    const todayCompleted = completedFlights.filter(f => {
      const flightDate = new Date(f.completedAt || f.scheduledDeparture);
      return flightDate >= startOfDay;
    });
    
    const todayCancelled = cancelledFlights.filter(f => {
      const flightDate = new Date(f.cancelledAt || f.scheduledDeparture);
      return flightDate >= startOfDay;
    });

    const todayEmergencies = emergencyFlights.filter(f => {
      const flightDate = new Date(f.completedAt || f.scheduledDeparture);
      return flightDate >= startOfDay;
    });

    const revenue = todayCompleted.reduce((sum, f) => sum + (f.revenue || 0), 0);
    const costs = todayCompleted.reduce((sum, f) => sum + (f.costs || 0), 0);
    const cancellationLosses = todayCancelled.reduce((sum, f) => sum + (f.cancellationCost?.totalCost || 0), 0);
    const emergencyLosses = todayEmergencies.reduce((sum, f) => sum + (f.emergencyLandingConsequences?.totalCost || f.costs || 0), 0);
    
    // Break down costs
    const fuelCosts = todayCompleted.reduce((sum, f) => sum + (f.fuelCost || f.costs * 0.35 || 0), 0);
    const crewCosts = todayCompleted.reduce((sum, f) => sum + (f.crewCost || f.costs * 0.20 || 0), 0);
    const maintenanceCosts = todayCompleted.reduce((sum, f) => sum + (f.maintenanceCost || f.costs * 0.15 || 0), 0);
    const otherCosts = costs - fuelCosts - crewCosts - maintenanceCosts;
    
    return {
      completedCount: todayCompleted.length,
      cancelledCount: todayCancelled.length,
      emergencyCount: todayEmergencies.length,
      revenue,
      costs,
      cancellationLosses,
      emergencyLosses,
      netProfit: revenue - costs - cancellationLosses - emergencyLosses,
      fuelCosts,
      crewCosts,
      maintenanceCosts,
      otherCosts,
      flights: todayCompleted,
      cancelledFlightsList: todayCancelled,
      emergencyFlightsList: todayEmergencies,
    };
  }, [completedFlights, cancelledFlights, emergencyFlights, worldState?.gameTime]);

  // All-time stats
  const allTimeStats = useMemo(() => {
    const revenue = completedFlights.reduce((sum, f) => sum + (f.revenue || 0), 0);
    const costs = completedFlights.reduce((sum, f) => sum + (f.costs || 0), 0);
    const cancellationLosses = cancelledFlights.reduce((sum, f) => sum + (f.cancellationCost?.totalCost || 0), 0);
    const emergencyLosses = emergencyFlights.reduce((sum, f) => sum + (f.emergencyLandingConsequences?.totalCost || f.costs || 0), 0);

    return {
      completedCount: completedFlights.length,
      cancelledCount: cancelledFlights.length,
      emergencyCount: emergencyFlights.length,
      revenue,
      costs,
      cancellationLosses,
      emergencyLosses,
      netProfit: revenue - costs - cancellationLosses - emergencyLosses,
    };
  }, [completedFlights, cancelledFlights, emergencyFlights]);

  // Current cash and stats
  const currentCash = worldState?.airline?.cash || 0;

  // Time display
  const gameTime = new Date(worldState?.gameTime || Date.now());
  const dateStr = gameTime.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric" });

  // Get monthly billing from systems
  const monthlyBilling = worldState?.financials?.lastMonthBilling || null;
  const macro = worldState?.macro || {};

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "#0a0a0a", color: "#e5e5e5" }}>
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b flex-shrink-0" style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a" }}>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/opscenter")}
            style={{ color: "#737373" }}
          >
            &lt; {t('financials.opsCenter')}
          </Button>
          <div>
            <h1 className="text-2xl font-bold" style={{ color: "#60ffd0" }}>{t('financials.title')}</h1>
            <p className="text-xs" style={{ color: "#737373" }}>{dateStr} | {t('financials.day')} {worldState?.dayNumber || 1}</p>
          </div>
        </div>

        {/* Current Balance */}
        <div className="flex items-center gap-6">
          <div className="text-right">
            <div className="text-xs uppercase" style={{ color: "#737373" }}>{t('financials.currentBalance')}</div>
            <div className="text-3xl font-bold font-mono" style={{ color: currentCash >= 0 ? "#22c55e" : "#ef4444" }}>
              {formatCurrency(currentCash)}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 p-6">
        {/* Top Stats Cards */}
        <div className="grid grid-cols-5 gap-4 mb-6">
          {/* Today's Revenue */}
          <Card style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a" }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase" style={{ color: "#737373" }}>{t('financials.todaysRevenue')}</span>
                <span className="text-xs font-bold" style={{ color: "#22c55e" }}>+</span>
              </div>
              <div className="text-2xl font-bold font-mono" style={{ color: "#22c55e" }}>
                {formatCurrency(todayStats.revenue)}
              </div>
              <div className="text-xs mt-1" style={{ color: "#525252" }}>
                {todayStats.completedCount} {t('financials.flights')}
              </div>
            </CardContent>
          </Card>

          {/* Today's Costs */}
          <Card style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a" }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase" style={{ color: "#737373" }}>{t('financials.todaysCosts')}</span>
                <span className="text-xs font-bold" style={{ color: "#ef4444" }}>-</span>
              </div>
              <div className="text-2xl font-bold font-mono" style={{ color: "#ef4444" }}>
                {formatCurrency(todayStats.costs)}
              </div>
              <div className="text-xs mt-1" style={{ color: "#525252" }}>
                {t('financials.operatingExpenses')}
              </div>
            </CardContent>
          </Card>

          {/* Cancellation Losses */}
          <Card style={{ backgroundColor: "#0c0c0c", borderColor: todayStats.cancellationLosses > 0 ? "#ef4444" : "#1a1a1a" }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase" style={{ color: "#737373" }}>{t('financials.cancellations')}</span>
                <span className="text-xs font-bold" style={{ color: todayStats.cancellationLosses > 0 ? "#ef4444" : "#525252" }}>X</span>
              </div>
              <div className="text-2xl font-bold font-mono" style={{ color: todayStats.cancellationLosses > 0 ? "#ef4444" : "#525252" }}>
                {todayStats.cancellationLosses > 0 ? `-${formatCurrency(todayStats.cancellationLosses)}` : "$0"}
              </div>
              <div className="text-xs mt-1" style={{ color: "#525252" }}>
                {todayStats.cancelledCount} {t('financials.cancelled')}
              </div>
            </CardContent>
          </Card>

          {/* Emergency Losses */}
          <Card style={{ backgroundColor: "#0c0c0c", borderColor: todayStats.emergencyLosses > 0 ? "#dc2626" : "#1a1a1a" }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase" style={{ color: "#737373" }}>{t('financials.emergencies')}</span>
                <span className="text-xs font-bold" style={{ color: todayStats.emergencyLosses > 0 ? "#dc2626" : "#525252" }}>!</span>
              </div>
              <div className="text-2xl font-bold font-mono" style={{ color: todayStats.emergencyLosses > 0 ? "#dc2626" : "#525252" }}>
                {todayStats.emergencyLosses > 0 ? `-${formatCurrency(todayStats.emergencyLosses)}` : "$0"}
              </div>
              <div className="text-xs mt-1" style={{ color: "#525252" }}>
                {todayStats.emergencyCount || 0} {t('financials.incidents')}
              </div>
            </CardContent>
          </Card>

          {/* Today's Net */}
          <Card style={{ backgroundColor: "#0c0c0c", borderColor: todayStats.netProfit >= 0 ? "#22c55e" : "#ef4444" }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase" style={{ color: "#737373" }}>{t('financials.todaysNet')}</span>
                <span className="text-xs font-bold" style={{ color: todayStats.netProfit >= 0 ? "#22c55e" : "#ef4444" }}>
                  {todayStats.netProfit >= 0 ? "+" : "-"}
                </span>
              </div>
              <div className="text-2xl font-bold font-mono" style={{ color: todayStats.netProfit >= 0 ? "#22c55e" : "#ef4444" }}>
                {formatCurrency(todayStats.netProfit, true)}
              </div>
              <div className="text-xs mt-1" style={{ color: "#525252" }}>
                {todayStats.netProfit >= 0 ? t('financials.profit') : t('financials.loss')}
              </div>
            </CardContent>
          </Card>

          {/* All-Time Profit */}
          <Card style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a" }}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs uppercase" style={{ color: "#737373" }}>{t('financials.allTimeNet')}</span>
                <span className="text-xs font-bold" style={{ color: "#60ffd0" }}>$</span>
              </div>
              <div className="text-2xl font-bold font-mono" style={{ color: allTimeStats.netProfit >= 0 ? "#60ffd0" : "#ef4444" }}>
                {formatCurrency(allTimeStats.netProfit, true)}
              </div>
              <div className="text-xs mt-1" style={{ color: "#525252" }}>
                {allTimeStats.completedCount} {t('financials.totalFlights')}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-3 gap-6">
          {/* Left: Flight-by-flight breakdown + Profit Chart */}
          <div className="col-span-2 space-y-6">
            <Card style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a" }}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2" style={{ color: "#60ffd0" }}>
                  {t('financials.flightRevenueDetails')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Simple tab buttons instead of Tabs component */}
                <div className="flex gap-2 mb-4">
                  <Button
                    size="sm"
                    variant={selectedTab === "today" ? "default" : "ghost"}
                    onClick={() => setSelectedTab("today")}
                    style={{
                      backgroundColor: selectedTab === "today" ? "#60ffd0" : "transparent",
                      color: selectedTab === "today" ? "#000" : "#737373"
                    }}
                  >
                    {t('financials.today')} ({todayStats.completedCount})
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedTab === "cancelled" ? "default" : "ghost"}
                    onClick={() => setSelectedTab("cancelled")}
                    style={{
                      backgroundColor: selectedTab === "cancelled" ? "#ef4444" : "transparent",
                      color: selectedTab === "cancelled" ? "#fff" : "#737373"
                    }}
                  >
                    {t('financials.cancellations')} ({cancelledFlights.length})
                  </Button>
                  <Button
                    size="sm"
                    variant={selectedTab === "all" ? "default" : "ghost"}
                    onClick={() => setSelectedTab("all")}
                    style={{
                      backgroundColor: selectedTab === "all" ? "#60ffd0" : "transparent",
                      color: selectedTab === "all" ? "#000" : "#737373"
                    }}
                  >
                    {t('financials.allTime')} ({completedFlights.length})
                  </Button>
                </div>

                {/* Today's Flights */}
                {selectedTab === "today" && (
                  todayStats.flights.length === 0 ? (
                    <div className="text-center py-8" style={{ color: "#525252" }}>
                      <div className="text-4xl font-bold mb-2 opacity-30">---</div>
                      <p>{t('financials.noCompletedToday')}</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {todayStats.flights.map((flight) => (
                        <FlightRevenueRow key={flight.id} flight={flight} t={t} />
                      ))}
                    </div>
                  )
                )}

                {/* Cancelled Flights */}
                {selectedTab === "cancelled" && (
                  cancelledFlights.length === 0 ? (
                    <div className="text-center py-8" style={{ color: "#525252" }}>
                      <div className="text-4xl font-bold mb-2 opacity-30">OK</div>
                      <p>{t('financials.noCancelled')}</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {cancelledFlights.map((flight) => (
                        <CancelledFlightRow key={flight.id} flight={flight} t={t} />
                      ))}
                    </div>
                  )
                )}

                {/* All Flights */}
                {selectedTab === "all" && (
                  completedFlights.length === 0 ? (
                    <div className="text-center py-8" style={{ color: "#525252" }}>
                      <div className="text-4xl font-bold mb-2 opacity-30">---</div>
                      <p>{t('financials.noCompletedYet')}</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {completedFlights.slice(0, 50).map((flight) => (
                        <FlightRevenueRow key={flight.id} flight={flight} t={t} />
                      ))}
                      {completedFlights.length > 50 && (
                        <div className="text-center py-2 text-xs" style={{ color: "#525252" }}>
                          {t('financials.showingFlights', { shown: 50, total: completedFlights.length })}
                        </div>
                      )}
                    </div>
                  )
                )}
              </CardContent>
            </Card>

            {/* PROFIT CHART */}
            <ProfitChart 
              financialHistory={worldState.financialHistory || []}
              currentDay={worldState.dayNumber || 1}
            />
          </div>

          {/* Right: Cost Breakdown */}
          <div className="space-y-4">
            {/* Cost Breakdown Card */}
            <Card style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a" }}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base" style={{ color: "#60ffd0" }}>
                  {t('financials.costBreakdown')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <CostBreakdownRow
                  abbrev="FUEL"
                  label={t('financials.fuel')}
                  amount={todayStats.fuelCosts}
                  total={todayStats.costs}
                  color="#f97316"
                />
                <CostBreakdownRow
                  abbrev="CRW"
                  label={t('financials.crew')}
                  amount={todayStats.crewCosts}
                  total={todayStats.costs}
                  color="#3b82f6"
                />
                <CostBreakdownRow
                  abbrev="MX"
                  label={t('financials.maintenance')}
                  amount={todayStats.maintenanceCosts}
                  total={todayStats.costs}
                  color="#eab308"
                />
                <CostBreakdownRow
                  abbrev="FEE"
                  label={t('financials.feesOther')}
                  amount={todayStats.otherCosts}
                  total={todayStats.costs}
                  color="#8b5cf6"
                />

                <div className="pt-2 border-t" style={{ borderColor: "#222" }}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium" style={{ color: "#e5e5e5" }}>{t('financials.totalOperatingCosts')}</span>
                    <span className="font-bold font-mono" style={{ color: "#ef4444" }}>
                      {formatCurrency(todayStats.costs)}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Macro Engine Card */}
            <Card style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a" }}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base" style={{ color: "#60ffd0" }}>
                  {t('financials.marketConditions')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: "#a3a3a3" }}>{t('financials.fuelPriceIndex')}</span>
                  <span className="font-mono font-bold" style={{
                    color: (macro.jetFuelPriceMultiplier || 1) > 1.1 ? "#ef4444" :
                           (macro.jetFuelPriceMultiplier || 1) < 0.9 ? "#22c55e" : "#eab308"
                  }}>
                    {((macro.jetFuelPriceMultiplier || 1) * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: "#a3a3a3" }}>{t('financials.laborCostIndex')}</span>
                  <span className="font-mono font-bold" style={{
                    color: (macro.laborCostMultiplier || 1) > 1.1 ? "#ef4444" : "#22c55e"
                  }}>
                    {((macro.laborCostMultiplier || 1) * 100).toFixed(0)}%
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm" style={{ color: "#a3a3a3" }}>{t('financials.demandIndex')}</span>
                  <span className="font-mono font-bold" style={{
                    color: (macro.demandMultiplier || 1) > 0.95 ? "#22c55e" : "#ef4444"
                  }}>
                    {((macro.demandMultiplier || 1) * 100).toFixed(0)}%
                  </span>
                </div>
              </CardContent>
            </Card>

            {/* Monthly Report Card */}
            {monthlyBilling && (
              <Card style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a" }}>
                <CardHeader className="pb-2">
                  <CardTitle className="flex items-center gap-2 text-base" style={{ color: "#60ffd0" }}>
                    {t('financials.lastMonthReport')}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span style={{ color: "#737373" }}>{t('financials.revenue')}</span>
                    <span className="font-mono" style={{ color: "#22c55e" }}>
                      {formatCurrency(monthlyBilling.revenue)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span style={{ color: "#737373" }}>{t('financials.costs')}</span>
                    <span className="font-mono" style={{ color: "#ef4444" }}>
                      {formatCurrency(monthlyBilling.costs)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm pt-2 border-t" style={{ borderColor: "#222" }}>
                    <span className="font-medium" style={{ color: "#e5e5e5" }}>{t('financials.netProfit')}</span>
                    <span className="font-mono font-bold" style={{ color: monthlyBilling.profit >= 0 ? "#22c55e" : "#ef4444" }}>
                      {formatCurrency(monthlyBilling.profit, true)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Key Metrics */}
            <Card style={{ backgroundColor: "#0c0c0c", borderColor: "#1a1a1a" }}>
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-base" style={{ color: "#60ffd0" }}>
                  {t('financials.keyMetrics')}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: "#737373" }}>{t('financials.avgRevenuePerFlight')}</span>
                  <span className="font-mono" style={{ color: "#22c55e" }}>
                    {todayStats.completedCount > 0
                      ? formatCurrency(todayStats.revenue / todayStats.completedCount)
                      : "$0"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: "#737373" }}>{t('financials.avgCostPerFlight')}</span>
                  <span className="font-mono" style={{ color: "#ef4444" }}>
                    {todayStats.completedCount > 0
                      ? formatCurrency(todayStats.costs / todayStats.completedCount)
                      : "$0"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span style={{ color: "#737373" }}>{t('financials.avgProfitPerFlight')}</span>
                  <span className="font-mono" style={{ color: "#60ffd0" }}>
                    {todayStats.completedCount > 0
                      ? formatCurrency((todayStats.revenue - todayStats.costs) / todayStats.completedCount)
                      : "$0"}
                  </span>
                </div>
                <div className="flex items-center justify-between text-sm pt-2 border-t" style={{ borderColor: "#222" }}>
                  <span style={{ color: "#737373" }}>{t('financials.cancellationRate')}</span>
                  <span className="font-mono" style={{
                    color: todayStats.cancelledCount > 0 ? "#ef4444" : "#22c55e"
                  }}>
                    {todayStats.completedCount + todayStats.cancelledCount > 0
                      ? `${((todayStats.cancelledCount / (todayStats.completedCount + todayStats.cancelledCount)) * 100).toFixed(1)}%`
                      : "0%"}
                  </span>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* News Ticker */}
      <NewsTicker worldState={worldState} />
    </div>
  );
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

function FlightRevenueRow({ flight, t }) {
  const profit = (flight.revenue || 0) - (flight.costs || 0);
  const margin = flight.revenue > 0 ? (profit / flight.revenue) * 100 : 0;

  return (
    <div className="flex items-center justify-between p-3 rounded"
      style={{ backgroundColor: "#111", borderLeft: `3px solid ${profit >= 0 ? "#22c55e" : "#ef4444"}` }}>
      <div className="flex items-center gap-4">
        <div>
          <div className="font-bold font-mono" style={{ color: "#e5e5e5" }}>{flight.flightNumber}</div>
          <div className="text-xs" style={{ color: "#737373" }}>{flight.origin} - {flight.destination}</div>
        </div>
        {flight.totalDelayMinutes > 0 && (
          <Badge style={{ backgroundColor: "#eab308", color: "#000" }}>+{flight.totalDelayMinutes}m</Badge>
        )}
      </div>
      <div className="flex items-center gap-6 text-right">
        <div>
          <div className="text-xs" style={{ color: "#737373" }}>{t('financials.revenue')}</div>
          <div className="font-mono" style={{ color: "#22c55e" }}>{formatCurrency(flight.revenue || 0)}</div>
        </div>
        <div>
          <div className="text-xs" style={{ color: "#737373" }}>{t('financials.costs')}</div>
          <div className="font-mono" style={{ color: "#ef4444" }}>{formatCurrency(flight.costs || 0)}</div>
        </div>
        <div style={{ minWidth: "80px" }}>
          <div className="text-xs" style={{ color: "#737373" }}>{t('financials.profit')}</div>
          <div className="font-mono font-bold" style={{ color: profit >= 0 ? "#22c55e" : "#ef4444" }}>
            {formatCurrency(profit, true)}
          </div>
        </div>
        <div style={{ minWidth: "50px" }}>
          <div className="text-xs" style={{ color: "#737373" }}>{t('financials.margin')}</div>
          <div className="font-mono text-sm" style={{ color: margin >= 20 ? "#22c55e" : margin >= 10 ? "#eab308" : "#ef4444" }}>
            {margin.toFixed(0)}%
          </div>
        </div>
      </div>
    </div>
  );
}

function CancelledFlightRow({ flight, t }) {
  const cost = flight.cancellationCost?.totalCost || 0;

  return (
    <div className="flex items-center justify-between p-3 rounded"
      style={{ backgroundColor: "#1a0a0a", borderLeft: "3px solid #ef4444" }}>
      <div className="flex items-center gap-4">
        <div>
          <div className="font-bold font-mono line-through" style={{ color: "#737373" }}>{flight.flightNumber}</div>
          <div className="text-xs line-through" style={{ color: "#525252" }}>{flight.origin} - {flight.destination}</div>
        </div>
        <Badge style={{ backgroundColor: "#ef4444", color: "#fff" }}>{t('financials.cancellations').toUpperCase()}</Badge>
      </div>
      <div className="flex items-center gap-6 text-right">
        <div>
          <div className="text-xs" style={{ color: "#737373" }}>{t('financials.refunds')}</div>
          <div className="font-mono text-sm" style={{ color: "#ef4444" }}>
            {formatCurrency(flight.cancellationCost?.refundCost || 0)}
          </div>
        </div>
        <div>
          <div className="text-xs" style={{ color: "#737373" }}>{t('financials.dotComp')}</div>
          <div className="font-mono text-sm" style={{ color: "#ef4444" }}>
            {formatCurrency(flight.cancellationCost?.dotCompensation || 0)}
          </div>
        </div>
        <div>
          <div className="text-xs" style={{ color: "#737373" }}>{t('financials.rebooking')}</div>
          <div className="font-mono text-sm" style={{ color: "#ef4444" }}>
            {formatCurrency(flight.cancellationCost?.rebookingCost || 0)}
          </div>
        </div>
        <div style={{ minWidth: "80px" }}>
          <div className="text-xs" style={{ color: "#737373" }}>{t('financials.totalLoss')}</div>
          <div className="font-mono font-bold" style={{ color: "#ef4444" }}>
            -{formatCurrency(cost)}
          </div>
        </div>
      </div>
    </div>
  );
}

function CostBreakdownRow({ abbrev, label, amount, total, color }) {
  const percentage = total > 0 ? (amount / total) * 100 : 0;

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-bold" style={{ color }}>{abbrev}</span>
          <span className="text-sm" style={{ color: "#a3a3a3" }}>{label}</span>
        </div>
        <span className="font-mono text-sm" style={{ color: "#e5e5e5" }}>{formatCurrency(amount)}</span>
      </div>
      <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: "#222" }}>
        <div
          className="h-full transition-all"
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
      <div className="text-xs text-right mt-0.5" style={{ color: "#525252" }}>{percentage.toFixed(0)}%</div>
    </div>
  );
}